import { sql } from "drizzle-orm";
import {
  boolean,
  check,
  foreignKey,
  index,
  integer,
  numeric,
  pgTable,
  text,
  timestamp,
  uniqueIndex,
  uuid,
} from "drizzle-orm/pg-core";

const tz = { withTimezone: true } as const;

// Snapshot of the ADS-005 row-builder contract. `@ease/db` cannot import the
// CRM package directly, so the proof test pins these against the upstream file.
export const ADS_005_BUDGET_RECOMMENDATION_STATES = [
  "pending",
  "approved",
  "applied",
  "rejected",
  "expired",
  "superseded",
] as const;

export const ADS_005_BUDGET_RECOMMENDATION_REJECTION_REASONS = [
  "low_confidence",
  "policy_override",
  "out_of_window",
  "manual_alternative",
  "other",
] as const;

export const ADS_005_BUDGET_RECOMMENDATION_PROVIDERS = [
  "meta",
  "google",
  "tiktok",
] as const;

export const ADS_005_BUDGET_RECOMMENDATION_DIRECTIONS = [
  "scale_up",
  "scale_down",
  "pause",
] as const;

export const ADS_005_BUDGET_RECOMMENDATION_CONFIDENCE_BANDS = [
  "low",
  "medium",
  "high",
] as const;

export const ADS_005_BUDGET_RECOMMENDATION_BASES = [
  "high_roas_scale_up",
  "low_roas_scale_down",
  "quality_drop_scale_down",
  "spend_cap_throttle",
  "below_floor_pause_candidate",
  "blackout_pause",
] as const;

export const ADS_005_BUDGET_RECOMMENDATION_EXPIRY_HOURS = 24;
export const ADS_005_BUDGET_RECOMMENDATION_BACKTEST_WINDOW_DAYS = 14;
export const ADS_005_MIN_AUTO_APPLY_CONFIDENCE = "0.85";

export const budgetRecommendations = pgTable(
  "budget_recommendations",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    campaignId: text("campaign_id").notNull(),
    provider: text("provider").notNull(),
    state: text("state").notNull().default("pending"),
    direction: text("direction").notNull(),
    currentBudgetCents: integer("current_budget_cents").notNull(),
    recommendedBudgetCents: integer("recommended_budget_cents").notNull(),
    shiftDeltaCents: integer("shift_delta_cents").notNull(),
    confidence: numeric("confidence", { precision: 3, scale: 2 }).notNull(),
    confidenceBand: text("confidence_band").notNull(),
    basis: text("basis").notNull(),
    projectedRevenueDeltaCents: integer(
      "projected_revenue_delta_cents",
    ).notNull(),
    generatedBy: text("generated_by").notNull().default("system"),
    autoApplyEligible: boolean("auto_apply_eligible").notNull().default(false),
    approvedBy: text("approved_by"),
    approvedAt: timestamp("approved_at", tz),
    appliedBy: text("applied_by"),
    appliedAt: timestamp("applied_at", tz),
    rejectedBy: text("rejected_by"),
    rejectedAt: timestamp("rejected_at", tz),
    rejectionReason: text("rejection_reason"),
    expiredAt: timestamp("expired_at", tz),
    supersededByRecommendationId: uuid("superseded_by_recommendation_id"),
    supersededAt: timestamp("superseded_at", tz),
    expiresAt: timestamp("expires_at", tz)
      .notNull()
      .default(sql`now() + interval '24 hours'`),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    uniqueIndex("budget_recommendations_tenant_id_uq").on(
      table.tenantId,
      table.id,
    ),
    uniqueIndex("budget_recommendations_pending_campaign_uq")
      .on(table.tenantId, table.campaignId)
      .where(sql`${table.state} = 'pending'`),
    index("budget_recommendations_tenant_state_idx").on(
      table.tenantId,
      table.state,
      table.createdAt,
    ),
    index("budget_recommendations_expires_idx")
      .on(table.expiresAt)
      .where(sql`${table.state} = 'pending'`),
    foreignKey({
      name: "budget_recommendations_superseded_by_fk",
      columns: [table.tenantId, table.supersededByRecommendationId],
      foreignColumns: [table.tenantId, table.id],
    }),
    check(
      "budget_recommendations_provider_ck",
      sql`${table.provider} IN ('meta', 'google', 'tiktok')`,
    ),
    check(
      "budget_recommendations_state_ck",
      sql`${table.state} IN ('pending', 'approved', 'applied', 'rejected', 'expired', 'superseded')`,
    ),
    check(
      "budget_recommendations_direction_ck",
      sql`${table.direction} IN ('scale_up', 'scale_down', 'pause')`,
    ),
    check(
      "budget_recommendations_confidence_band_ck",
      sql`${table.confidenceBand} IN ('low', 'medium', 'high')`,
    ),
    check(
      "budget_recommendations_basis_ck",
      sql`${table.basis} IN ('high_roas_scale_up', 'low_roas_scale_down', 'quality_drop_scale_down', 'spend_cap_throttle', 'below_floor_pause_candidate', 'blackout_pause')`,
    ),
    check(
      "budget_recommendations_confidence_ck",
      sql`${table.confidence} >= 0 AND ${table.confidence} <= 1`,
    ),
    check(
      "budget_recommendations_budget_non_negative_ck",
      sql`${table.currentBudgetCents} >= 0 AND ${table.recommendedBudgetCents} >= 0`,
    ),
    check(
      "budget_recommendations_shift_delta_consistency_ck",
      sql`${table.recommendedBudgetCents} - ${table.currentBudgetCents} = ${table.shiftDeltaCents}`,
    ),
    check(
      "budget_recommendations_direction_budget_consistency_ck",
      sql`
        (
          ${table.direction} = 'pause'
          AND ${table.currentBudgetCents} > 0
          AND ${table.recommendedBudgetCents} = 0
          AND ${table.shiftDeltaCents} = -${table.currentBudgetCents}
        ) OR (
          ${table.direction} = 'scale_up'
          AND ${table.shiftDeltaCents} > 0
          AND ${table.recommendedBudgetCents} > ${table.currentBudgetCents}
        ) OR (
          ${table.direction} = 'scale_down'
          AND ${table.shiftDeltaCents} < 0
          AND ${table.recommendedBudgetCents} < ${table.currentBudgetCents}
          AND ${table.recommendedBudgetCents} > 0
        )
      `,
    ),
    check(
      "budget_recommendations_basis_direction_consistency_ck",
      sql`
        (
          ${table.basis} IN ('high_roas_scale_up', 'spend_cap_throttle')
          AND ${table.direction} = 'scale_up'
        ) OR (
          ${table.basis} IN ('low_roas_scale_down', 'quality_drop_scale_down')
          AND ${table.direction} = 'scale_down'
        ) OR (
          ${table.basis} IN ('below_floor_pause_candidate', 'blackout_pause')
          AND ${table.direction} = 'pause'
        )
      `,
    ),
    check(
      "budget_recommendations_generated_by_ck",
      sql`length(btrim(${table.generatedBy})) > 0`,
    ),
    check(
      "budget_recommendations_auto_apply_threshold_ck",
      sql`${table.autoApplyEligible} = false OR ${table.confidence} >= ${ADS_005_MIN_AUTO_APPLY_CONFIDENCE}`,
    ),
    check(
      "budget_recommendations_rejection_reason_ck",
      sql`${table.rejectionReason} IS NULL OR ${table.rejectionReason} IN ('low_confidence', 'policy_override', 'out_of_window', 'manual_alternative', 'other')`,
    ),
    check(
      "budget_recommendations_superseded_self_ref_ck",
      sql`${table.supersededByRecommendationId} IS NULL OR ${table.supersededByRecommendationId} <> ${table.id}`,
    ),
    check(
      "budget_recommendations_lifecycle_consistency_ck",
      sql`
        (
          ${table.state} = 'pending'
          AND ${table.approvedBy} IS NULL
          AND ${table.approvedAt} IS NULL
          AND ${table.appliedBy} IS NULL
          AND ${table.appliedAt} IS NULL
          AND ${table.rejectedBy} IS NULL
          AND ${table.rejectedAt} IS NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.expiredAt} IS NULL
          AND ${table.supersededByRecommendationId} IS NULL
          AND ${table.supersededAt} IS NULL
        ) OR (
          ${table.state} = 'approved'
          AND ${table.approvedBy} IS NOT NULL
          AND ${table.approvedAt} IS NOT NULL
          AND ${table.appliedBy} IS NULL
          AND ${table.appliedAt} IS NULL
          AND ${table.rejectedBy} IS NULL
          AND ${table.rejectedAt} IS NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.expiredAt} IS NULL
          AND ${table.supersededByRecommendationId} IS NULL
          AND ${table.supersededAt} IS NULL
        ) OR (
          ${table.state} = 'applied'
          AND ${table.approvedBy} IS NOT NULL
          AND ${table.approvedAt} IS NOT NULL
          AND ${table.appliedBy} IS NOT NULL
          AND ${table.appliedAt} IS NOT NULL
          AND ${table.rejectedBy} IS NULL
          AND ${table.rejectedAt} IS NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.expiredAt} IS NULL
          AND ${table.supersededByRecommendationId} IS NULL
          AND ${table.supersededAt} IS NULL
        ) OR (
          ${table.state} = 'rejected'
          AND ${table.approvedBy} IS NULL
          AND ${table.approvedAt} IS NULL
          AND ${table.appliedBy} IS NULL
          AND ${table.appliedAt} IS NULL
          AND ${table.rejectedBy} IS NOT NULL
          AND ${table.rejectedAt} IS NOT NULL
          AND ${table.rejectionReason} IS NOT NULL
          AND ${table.expiredAt} IS NULL
          AND ${table.supersededByRecommendationId} IS NULL
          AND ${table.supersededAt} IS NULL
        ) OR (
          ${table.state} = 'expired'
          AND ${table.approvedBy} IS NULL
          AND ${table.approvedAt} IS NULL
          AND ${table.appliedBy} IS NULL
          AND ${table.appliedAt} IS NULL
          AND ${table.rejectedBy} IS NULL
          AND ${table.rejectedAt} IS NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.expiredAt} IS NOT NULL
          AND ${table.supersededByRecommendationId} IS NULL
          AND ${table.supersededAt} IS NULL
        ) OR (
          ${table.state} = 'superseded'
          AND ${table.approvedBy} IS NULL
          AND ${table.approvedAt} IS NULL
          AND ${table.appliedBy} IS NULL
          AND ${table.appliedAt} IS NULL
          AND ${table.rejectedBy} IS NULL
          AND ${table.rejectedAt} IS NULL
          AND ${table.rejectionReason} IS NULL
          AND ${table.expiredAt} IS NULL
          AND ${table.supersededByRecommendationId} IS NOT NULL
          AND ${table.supersededAt} IS NOT NULL
        )
      `,
    ),
  ],
);

export const budgetRecommendationBacktest = pgTable(
  "budget_recommendation_backtest",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    recommendationId: uuid("recommendation_id").notNull(),
    measuredAt: timestamp("measured_at", tz).notNull(),
    actualSpendCents: integer("actual_spend_cents").notNull(),
    actualRevenueCents: integer("actual_revenue_cents").notNull(),
    actualConversions: integer("actual_conversions").notNull(),
    driftScore: numeric("drift_score", { precision: 6, scale: 4 }).notNull(),
    windowDays: integer("window_days")
      .notNull()
      .default(ADS_005_BUDGET_RECOMMENDATION_BACKTEST_WINDOW_DAYS),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    foreignKey({
      name: "budget_recommendation_backtest_tenant_recommendation_fk",
      columns: [table.tenantId, table.recommendationId],
      foreignColumns: [budgetRecommendations.tenantId, budgetRecommendations.id],
    }).onDelete("cascade"),
    uniqueIndex("budget_recommendation_backtest_measurement_uq").on(
      table.tenantId,
      table.recommendationId,
      table.measuredAt,
    ),
    check(
      "budget_recommendation_backtest_actuals_non_negative_ck",
      sql`${table.actualSpendCents} >= 0 AND ${table.actualRevenueCents} >= 0 AND ${table.actualConversions} >= 0`,
    ),
    check(
      "budget_recommendation_backtest_window_days_ck",
      sql`${table.windowDays} > 0`,
    ),
  ],
);
